import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ProtectedRoute = ({ children, allowedRoles, requiredPermission }) => {
  const { user, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin always has access
  if (user.role === 'Admin') {
    return children;
  }

  // Check permission-based access first (new system)
  if (requiredPermission) {
    if (hasPermission(requiredPermission, 'view')) {
      return children;
    }
  }

  // Fallback to role-based access (for backward compatibility)
  if (allowedRoles && allowedRoles.includes(user.role)) {
    return children;
  }

  // If neither permission nor role matches, check if any permission is granted
  // This handles custom roles that have permissions assigned
  if (requiredPermission && !hasPermission(requiredPermission, 'view')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
          <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
          <p className="mt-1 text-sm text-gray-500">Contact your administrator to request access.</p>
        </div>
      </div>
    );
  }

  // For routes with only allowedRoles (no requiredPermission), deny if role doesn't match
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
          <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
          <p className="mt-1 text-sm text-gray-500">Contact your administrator to request access.</p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
